import { Currency, EventSupportCurrencyProvider } from "@src/@types/api.d";
import Icon from "@src/components/Icon";
import { copyToClipboard, objectKeyToWord } from "@src/utils/general";
import * as React from "react";
import styles from "../cashGift.module.scss";
import ForeignDetailsForm from "./ForeignDetailsForm";
import { AvailablePaymentCurrencies } from "@src/components/BirthdayTemplate1/AsoEbiLink";
import { ForeignAccountDetailType } from "..";

interface CurrencyProviderDetails {
  provider: NonNullable<AvailablePaymentCurrencies>[number]["provider"];
  sub_account_id: string;
  sub_account_code: string;
}

interface GroupedCurrencyConfig {
  currency: Currency;
  providers: CurrencyProviderDetails[];
}

interface ForeignDetailsInterface {
  foreignDetail: ForeignAccountDetailType | undefined;
  eventId: number;
  eventUuid: string;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  initialAmount?: number | string;
  itemName?: string;
  foreignCurrency: Currency | null;
  availableGiftPaymentCurrencies?: AvailablePaymentCurrencies;
}

const ForeignDetails = ({
  eventId,
  eventUuid,
  setOpen,
  initialAmount,
  itemName,
  foreignDetail,
  foreignCurrency,
  availableGiftPaymentCurrencies,
}: ForeignDetailsInterface) => {
  const [showAccountDetails, setShowAccountDetails] =
    React.useState<boolean>(false);

  // Group currencies by currency value, collecting all providers and their details
  const groupedCurrencies = React.useMemo<GroupedCurrencyConfig[]>(() => {
    if (!availableGiftPaymentCurrencies) return [];

    const currencyMap = new Map<Currency, CurrencyProviderDetails[]>();

    availableGiftPaymentCurrencies.forEach((item) => {
      if (!item) return;

      const { currency, provider, sub_account_id, sub_account_code } = item;

      if (!currencyMap.has(currency)) {
        currencyMap.set(currency, []);
      }

      currencyMap.get(currency)?.push({
        provider,
        sub_account_id,
        sub_account_code,
      });
    });

    return Array.from(currencyMap.entries()).map(([currency, providers]) => ({
      currency,
      providers,
    }));
  }, [availableGiftPaymentCurrencies]);

  // Find the currency configuration for the selected currency
  const currencyConfig = React.useMemo(() => {
    if (!foreignCurrency) return null;
    return (
      groupedCurrencies.find((config) => config.currency === foreignCurrency) ||
      null
    );
  }, [foreignCurrency, groupedCurrencies]);

  // Check which providers exist for this currency
  const hasStripe = React.useMemo(() => {
    if (!currencyConfig) return false;
    return currencyConfig.providers.some(
      (provider) => provider.provider === EventSupportCurrencyProvider.Stripe
    );
  }, [currencyConfig]);

  const hasBankDetails = React.useMemo(() => {
    if (!currencyConfig) return false;
    return currencyConfig.providers.some(
      (provider) =>
        provider.provider === EventSupportCurrencyProvider.BankDetails
    );
  }, [currencyConfig]);

  const hasBothProviders = hasStripe && hasBankDetails;

  // If only STRIPE provider, show ForeignDetailsForm only (no toggle)
  if (hasStripe && !hasBankDetails) {
    return (
      <ForeignDetailsForm
        eventId={eventId}
        setOpen={setOpen}
        eventUuid={eventUuid}
        currency={foreignCurrency}
        initialAmount={initialAmount}
        itemName={itemName}
        availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
      />
    );
  }

  // If only BANK_DETAILS provider, show ForeignAccountDetails only (no toggle)
  if (hasBankDetails && !hasStripe) {
    return <ForeignAccountDetails foreignDetail={foreignDetail} />;
  }

  // If both STRIPE and BANK_DETAILS, show ForeignDetailsForm first with toggle
  if (hasBothProviders) {
    return (
      <div className="bg-white rounded-md">
        {!showAccountDetails && (
          <ForeignDetailsForm
            eventId={eventId}
            setOpen={setOpen}
            eventUuid={eventUuid}
            currency={foreignCurrency}
            initialAmount={initialAmount}
            itemName={itemName}
            availableGiftPaymentCurrencies={availableGiftPaymentCurrencies}
          />
        )}

        {showAccountDetails && (
          <ForeignAccountDetails foreignDetail={foreignDetail} />
        )}

        <div className="text-right px-4">
          {!!foreignDetail && (
            <button
              className="text-blue-500 underline text-sm hover:text-blue-600 transition duration-300"
              onClick={() => setShowAccountDetails(!showAccountDetails)}
            >
              {showAccountDetails
                ? "Show Gift Form"
                : "Show Account Details instead"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Fallback: if no matching providers, show account details
  return <ForeignAccountDetails foreignDetail={foreignDetail} />;
};

export default ForeignDetails;

const ForeignAccountDetails = ({
  foreignDetail,
}: {
  foreignDetail: ForeignAccountDetailType | undefined;
}) => {
  const uppercaseCurrency = (currency: Currency) =>
    currency?.toLocaleUpperCase();
  return (
    <div className={styles["cashGift--content"]}>
      <div className={styles["cashGift--content__top"]}>
        <h1>{`${foreignDetail?.currency?.toLocaleUpperCase()} Account Details`}</h1>
      </div>
      <div className={styles["cashGift--content__foreignDetails"]}>
        {Object.entries(foreignDetail ?? {})
          ?.filter(
            ([key, value]) =>
              !(
                key === "event_id" ||
                key === "__typename" ||
                key === "walletId"
              )
          )
          .map(([key, value], index) => (
            <div
              className={styles["cashGift--content__foreignDetails__detail"]}
              key={index}
            >
              <div className={styles["name"]}>{objectKeyToWord(key)}</div>
              <div
                className={styles["value"]}
                onClick={() => {
                  copyToClipboard(value as string, `${key} has been copied!`);
                }}
              >
                {" "}
                <span>
                  {key === "currency"
                    ? uppercaseCurrency(value as Currency)
                    : value}
                </span>{" "}
                <Icon iconName="copyIcon" />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
